import { UrlPathUtils } from "@/utils";
import {linkItems} from "@/components/Header/types/LinkItemsCollection";
import { type RouteLocationNormalized } from 'vue-router';
import { useLinesVodStore } from "@/stores/LinesVod";
import { useLinesVodGenresStore } from "@/stores/LinesVodGenres";
import { useMultiLinesVodGenresStore } from "@/stores/MultiLinesVodGenres";
import { useLinesVodDetailGenreStore } from "@/stores/LinesVodDetailGenre";
import { useMostPopularVod } from "@/stores/MostPopularVod";
import { useLinesHomeStore } from "@/stores/LinesHome";
import { useHomePageStore } from "@/stores/HomePage";
import { useLinesMyTvStore } from "@/stores/LinesMyTv";
import { useLinesDetailMovieStore } from "@/stores/LinesDetailMovie";
import { useLinesDetailProgramStore } from "@/stores/LinesDetailProgram";
import { useLinesDetailShowStore } from "@/stores/LinesDetailShow";
import { useDetailProgramPageStore } from "@/stores/DetailProgramPage";
import { useDetailShowPageStore } from "@/stores/DetailShowPage";
import { useDetailPageStore } from "@/stores/DetailPage";
import { useGuideStore } from "@/stores/Guide";
import { useFavorite } from "@/composables/useFavorite";
import { useVodPageStore } from "@/stores/VodPage";
import { useSearchPageStore } from "@/stores/SearchPage";
import { useApplicationStore } from "@/stores/Application";
import { useCastPageStore } from "@/stores/CastPage";


export const useClearStoreByRoute = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    if (!import.meta.client || !to || !from) {
        return;
    }
    const ApplicationStore = useApplicationStore();
    const { setBasicRoute } = ApplicationStore;

    const basicPages: Array<string> = [
        linkItems.home.routerPath,
        linkItems.guide.routerPath, 
        linkItems.myTv.routerPath, 
        linkItems.onDemand.routerPath, 
        linkItems.search.routerPath, 
        linkItems.settings.routerPath,
        linkItems.login.routerPath,
        linkItems.logInWelcomeScreen.routerPath
    ];

    if (basicPages.includes(to.path)) {
        setBasicRoute(to.path);
    }

    const toFullPath = to.fullPath;
    const fromFullPath = from.fullPath;


    const {clearAllLinesDetailMovie} = useLinesDetailMovieStore();
    const { clearAllDetailShowPage } = useDetailShowPageStore();
    const {clearAllLinesDetailShow} = useLinesDetailShowStore();
    const {clearAllLinesDetailProgram} = useLinesDetailProgramStore();
    const { clearAllDetailProgramPage } = useDetailProgramPageStore(); 
    const { clearAllSearchPage, clearShowAllSearch, clearPopularWords } = useSearchPageStore();

    const { clearAllDetailPage } = useDetailPageStore();

    const { clearAllCastPage } = useCastPageStore();
    
    const { deleteSavedIdsFavorites } = useFavorite();
    
    if (UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.onDemand.routerPath, basicPages)) {
        const { clearAllLinesVodStore } = useLinesVodStore();
        const { clearAllLinesVodGenresStore } = useLinesVodGenresStore();
        const { clearMultiLinesVodGenres } = useMultiLinesVodGenresStore();
        const { clearAllLinesVodDetailGenreStore } = useLinesVodDetailGenreStore();
        const { clearAllMostPopularVod } = useMostPopularVod();
        const { clearAllVodPage } = useVodPageStore();

        clearAllVodPage();
        clearAllMostPopularVod();
        clearAllLinesVodStore();
        clearAllLinesVodGenresStore();
        clearMultiLinesVodGenres();
        clearAllLinesVodDetailGenreStore();
        clearAllCastPage();
    }

    if (UrlPathUtils.pathIncludesPages(toFullPath, fromFullPath, linkItems.home.routerPath, basicPages)) {
        const {clearAllLinesHome} = useLinesHomeStore();
        const {clearAllHomePage} = useHomePageStore();

        clearAllLinesHome();
        clearAllHomePage();
    }
    
    if (UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.guide.routerPath, basicPages)) {
        const {clearAll: clearAllGuide} = useGuideStore();
        clearAllGuide();
    }

    if (UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.search.routerPath, basicPages)) {
        clearAllSearchPage();
        clearPopularWords();
        clearAllCastPage();
    }

    const pageShowAllSearch = [...basicPages, linkItems.showAllSearch.routerPath];
    if (UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.showAllSearch.routerPath, pageShowAllSearch)) {
        clearShowAllSearch();
    }

    if (UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.myTv.routerPath, basicPages)) {
        void deleteSavedIdsFavorites();

        const { clearAll: clearAllMyTv } = useLinesMyTvStore();
        clearAllMyTv();
        clearAllCastPage();
    }

    const pageShowAllHome = [...basicPages, linkItems.showAllHome.routerPath];
    if (UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.showAllHome.routerPath, pageShowAllHome)) {
        const {clearShowAllHome} = useHomePageStore();

        clearShowAllHome();
    }

    const pageShowAllVod = [
        ...basicPages, 
        linkItems.showAllVod.routerPath, 
        linkItems.showAllForGenre.routerPath, 
        linkItems.showAllChannels.routerPath, 
        linkItems.subgenres.routerPath + '/' + to.params.id, 
        linkItems.subgenresPremium.routerPath + '/' + to.params.id, 
        linkItems.detailGenre.routerPath + '/' + to.params.id, 
    ];
    if (
        UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.showAllVod.routerPath, pageShowAllVod) 
        || UrlPathUtils.pathIncludesPages(to.path, from.path, linkItems.showAllForGenre.routerPath, pageShowAllVod)
    ) {
        const {clearShowAllVod} = useVodPageStore();

        clearShowAllVod();
    }


    const pagesForDetails = [
        ...basicPages, 
        `${linkItems.subgenres.routerPath + '/' + to.params.id}`,
        `${linkItems.subgenresPremium.routerPath + '/' + to.params.id}`,
        linkItems.showAllVod.routerPath,
        linkItems.showAllOnDemand.routerPath,
        linkItems.showAllPrograms.routerPath,
        linkItems.showAllFavChannels.routerPath,
        linkItems.showAllForGenre.routerPath,
        linkItems.showAllSearch.routerPath,
    ];
    if (UrlPathUtils.pathIncludesPages(to.path, from.path, `${linkItems.detailsVodMovies.routerPath + '/' + from.params.id}`, pagesForDetails)
        || UrlPathUtils.pathIncludesPages(to.path, from.path, `${linkItems.detailsVodSeries.routerPath + '/' + from.params.id}`, pagesForDetails)
        || UrlPathUtils.pathIncludesPages(to.path, from.path, `${linkItems.detailsPrograms.routerPath + '/' + from.params.id}`, pagesForDetails)
        ) {
            clearAllLinesDetailMovie();
            clearAllDetailShowPage();
            clearAllLinesDetailShow();
            clearAllLinesDetailProgram();
            clearAllDetailProgramPage();
            clearAllDetailPage();
    }
}